import { css } from 'styled-components';
import SVGInline from 'components/SVGInline/SVGInline';
import { styled } from 'components/ThemeConfigConnector/ThemeConfigConnnector';
import { DropdownContent, DropdownMenuList, PLAYER_DROPDOWN_BOTTOM_POSITION } from 'components/DropdownMenu/DropdownMenu.css';
import { ArrowIcon, ArrowLeft, ArrowRight, LaneScroller, NavigationArrow } from 'components/Lane/Lane.css';
import { Breakpoints, MQ, MQ_NOT_TOUCH } from 'style/styled-components/cssMediaQueries';
import { fontBold, maxZIndex } from 'style/styled-components/cssGlobalVariables';
import { ContentMarker } from 'style/styled-components/reusable.css';
import { hexToRgba } from 'utils/fnColors';

const EPISODE_SELECTOR_WIDTH = '600px';
const EPISODE_SELECTOR_MIN_HEIGHT = '100px';
const EPISODE_SELECTOR_MAX_HEIGHT = '550px';
const EPISODE_THUMBNAIL_WIDTH = '180px';
const EPISODE_THUMBNAIL_MOBILE_WIDTH = '120px';
const EPISODE_LIST_GRADIENT_HEIGHT = '25px';
const EPISODE_LOCK_ICON_SIZE = '16px';

export const EpisodeSelectorWrapper = styled.div`
    position: relative;
    z-index: 5;
    cursor: initial;

    ${DropdownContent} {
        ${MQ(Breakpoints.s)} {
            left: auto;
            right: -7px;
            bottom: ${PLAYER_DROPDOWN_BOTTOM_POSITION};
            width: ${EPISODE_SELECTOR_WIDTH};
            min-height: ${EPISODE_SELECTOR_MIN_HEIGHT};
            max-height: ${EPISODE_SELECTOR_MAX_HEIGHT};
            padding: 0;
        }
    }

    ${DropdownMenuList} {
        ${MQ(Breakpoints.s)} {
            padding-top: 0;
            max-height: ${EPISODE_SELECTOR_MAX_HEIGHT};
            overflow: hidden;
        }
    }
`;

export const EpisodeSelectorContentWrapper = styled.div`
    text-align: left;
    display: flex;
    flex-direction: column;

    ${MQ(Breakpoints.s)} {
        min-height: ${EPISODE_SELECTOR_MIN_HEIGHT};
        max-height: ${EPISODE_SELECTOR_MAX_HEIGHT};
    }
`;

export const SeasonsRow = styled.div`
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
    padding: 16px;
    background: var(--color-dark-foreground-5);
`;

export const SeasonsLabel = styled.span`
    font-size: 20px;
    line-height: 24px;
    font-family: ${fontBold};
    color: ${(props) => props.theme.brandedConfig.high_contrast};
    flex-shrink: 0;
`;

export const SeasonsLaneWrapper = styled.div`
    flex: 1;
    min-width: 0;

    ${LaneScroller} {
        display: flex;
        gap: 4px;
        margin: 0;
        padding: 2px; /* for :focus */
    }

    ${NavigationArrow} {
        width: 24px;
        height: 24px;
        top: 50%;
        transform: translateY(-50%);
        margin: 0;
        padding: 0;
        background: transparent;

        ${ArrowIcon} {
            left: 50%;
            right: auto;
            top: 50%;
            transform: translate(-50%, -50%);

            svg {
                width: 8px;
                height: 16px;
            }
        }
    }

    ${ArrowLeft} {
        left: 0;
    }

    ${ArrowRight} {
        right: 0;
    }
`;

export const EpisodeListWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

export const EpisodeList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 8px 16px 16px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--color-dark-body-20) transparent;

    &::-webkit-scrollbar {
        width: 4px;
        background-color: transparent;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: var(--color-dark-body-20);
        border-radius: 2px;
    }
`;

export const EpisodeListGradient = styled.div<{ isBottom: boolean; isVisible: boolean }>`
    position: absolute;
    left: 0;
    right: 0;
    height: ${EPISODE_LIST_GRADIENT_HEIGHT};
    z-index: 1;
    pointer-events: none;
    opacity: ${(props) => (props.isVisible ? 1 : 0)};
    transition: opacity 0.2s ease-in-out;
    ${(props) => (props.isBottom ? 'bottom: 0;' : 'top: 0;')}
    background: linear-gradient(
        ${(props) => (props.isBottom ? 'to top' : 'to bottom')},
        var(--episode-list-fade-color, var(--color-dark-foreground-5)),
        transparent
    );
`;

export const EpisodeThumbnail = styled.div`
    position: relative;
    box-sizing: content-box;
    width: ${EPISODE_THUMBNAIL_MOBILE_WIDTH};
    flex-shrink: 0;

    ${MQ(Breakpoints.s)} {
        width: ${EPISODE_THUMBNAIL_WIDTH};
    }
    aspect-ratio: 16 / 9;
    border: 2px solid transparent;
    border-radius: 4px;
    outline: 2px solid transparent;
    margin: -2px 0 0 -2px;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 4px;
    }
`;

export const EpisodeRow = styled.div<{ isSelectable: boolean; isUpcoming: boolean }>`
    display: flex;
    gap: 16px;
    cursor: ${(props) => (props.isSelectable ? 'pointer' : 'default')};

    ${(props) =>
        props.isUpcoming &&
        css`
            ${EpisodeThumbnail} {
                &::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: #131320;
                    opacity: 0.5;
                    border-radius: 4px;
                    pointer-events: none;
                }
            }

            ${ContentMarker} {
                background-color: #2e2f42;
            }
        `}

    &:focus-visible {
        outline: none;

        ${EpisodeThumbnail} {
            outline: var(--focus-outline);
        }
    }

    ${MQ_NOT_TOUCH} {
        &:hover {
            ${EpisodeThumbnail} {
                outline-color: ${(props) => (props.isSelectable ? 'var(--color-dark-body)' : 'transparent')};
            }
        }
    }
`;

export const EpisodeInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

export const EpisodeLockIcon = styled(SVGInline)`
    display: block;
    flex-shrink: 0;
    height: ${EPISODE_LOCK_ICON_SIZE} !important;
    width: ${EPISODE_LOCK_ICON_SIZE} !important;
    margin-right: 6px;

    svg {
        display: block;
        height: ${EPISODE_LOCK_ICON_SIZE};
        width: ${EPISODE_LOCK_ICON_SIZE};
    }
`;

export const EpisodeTitle = styled.p`
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 20px;
    font-family: ${fontBold};
    color: ${(props) => props.theme.brandedConfig.high_contrast};
`;

export const EpisodeDescription = styled.p`
    font-size: 14px;
    line-height: 20px;
    color: ${(props) => hexToRgba(props.theme.brandedConfig.high_contrast, 0.7)};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

export const EpisodeAiringTime = styled.p`
    display: flex;
    gap: 12px;
    font-size: 14px;
    line-height: 20px;
    color: ${(props) => hexToRgba(props.theme.brandedConfig.high_contrast, 0.7)};
`;

export const EpisodeSelectorMobileWrapper = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    z-index: ${maxZIndex};
    width: 100%;
    height: 100%;
    background: ${(props) => hexToRgba(props.theme.brandedConfig.low_contrast, 0.9)};
    --episode-list-fade-color: ${(props) => hexToRgba(props.theme.brandedConfig.low_contrast, 0.9)};

    ${EpisodeSelectorContentWrapper} {
        padding-top: 64px;
        height: 100%;
        max-height: none;
    }

    ${SeasonsRow} {
        background: transparent;
    }
`;

export const EpisodeSelectorMobileCloseButton = styled.a`
    height: 27px;
    width: 27px;
    display: block;
    z-index: 15;
    cursor: pointer;
    line-height: 24px;
    opacity: 1;
    position: absolute;
    right: 18.67px;
    top: 18.67px;
    font-size: 24px;

    ${MQ(Breakpoints.xs)} {
        right: 26.67px;
        top: 26.67px;
    }

    .SVGInline,
    .SVGInline-svg {
        width: 100%;
        height: 100%;
        display: block;
    }
`;

export const EpisodesEmptyState = styled.p`
    padding: 8px 16px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${(props) => hexToRgba(props.theme.brandedConfig.high_contrast, 0.7)};
`;
